import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  logout, readLoginMarker, clearLoginMarker, LOGIN_MARKER_KEY, setOnUnauthorized, startNewSession,
} from './api.js';
import { useAutoLogout, DEFAULT_IDLE_TIMEOUT_MS } from './useAutoLogout.js';
import { useSessionHeartbeat, pingSession } from './useSessionHeartbeat.js';
import { useSessionExpiry } from './useSessionExpiry.js';

const POST_LOGIN_REDIRECT_KEY = 'af_post_login_redirect';

// Remembers where to go after logging in, unless something is remembered already.
export function rememberPostLoginRedirect(path) {
  if (path !== '/' && !sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY)) {
    sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, path);
  }
}

// Everything about the portal login session: logging in and out, the idle logout +
// keep-alive, the MaxTokenValidity notice, and keeping every tab in sync. The session token
// itself is an HttpOnly cookie; loginId is the non-secret marker of the current login.
export function useSession() {
  const navigate = useNavigate();
  const location = useLocation();

  const [loginId, setLoginId] = useState(() => readLoginMarker());
  const loginIdRef = useRef(loginId);
  loginIdRef.current = loginId;

  // Back to the login page without touching the server, remembering the current page
  // for after the next login.
  const endLocalSession = useCallback(() => {
    const currentPath = location.pathname + location.search;
    if (currentPath !== '/') sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, currentPath);

    startNewSession();
    setLoginId(null);
    navigate('/');
  }, [navigate, location]);

  const logOut = useCallback(async () => {
    const ownLoginId = loginIdRef.current;
    startNewSession(); // stop the old session's requests before logging out
    await logout();
    clearLoginMarker(ownLoginId);
    endLocalSession();
  }, [endLocalSession]);

  // On a 401 the server already ended the session. No /auth/logout here: that would end
  // whatever session the cookie holds by then, possibly a newer login.
  const expireLocally = useCallback(() => {
    if (!loginIdRef.current) return;
    clearLoginMarker(loginIdRef.current);
    endLocalSession();
  }, [endLocalSession]);
  const expireLocallyRef = useRef(expireLocally);
  expireLocallyRef.current = expireLocally;
  useEffect(() => {
    setOnUnauthorized(() => expireLocallyRef.current());
    return () => setOnUnauthorized(null);
  }, []);

  const logIn = useCallback((newLoginId) => {
    startNewSession();
    setLoginId(newLoginId);
    const redirect = sessionStorage.getItem(POST_LOGIN_REDIRECT_KEY);
    if (redirect) {
      sessionStorage.removeItem(POST_LOGIN_REDIRECT_KEY);
      navigate(redirect, { replace: true });
    }
  }, [navigate]);

  // Session limits the server enforces - reported by /me once the app has loaded it.
  const [idleTimeoutMs, setIdleTimeoutMs] = useState(DEFAULT_IDLE_TIMEOUT_MS);
  const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
  const setSessionLimits = useCallback(({ timeoutMs, expiresAt }) => {
    if (timeoutMs) setIdleTimeoutMs(timeoutMs);
    setSessionExpiresAt(expiresAt);
  }, []);

  const { secondsLeft, keepAlive } = useAutoLogout(loginId, logOut, idleTimeoutMs);
  useSessionHeartbeat(loginId, idleTimeoutMs);
  const { noticeShown: sessionEndNoticeShown, dismissNotice: dismissSessionEndNotice } =
    useSessionExpiry(loginId, sessionExpiresAt, logOut);

  // Pings right away too - otherwise the server session may still lapse and the next
  // request 401 despite the dismissed modal.
  const staySignedIn = useCallback(() => {
    keepAlive();
    pingSession();
  }, [keepAlive]);

  // Tab sync: a logout elsewhere ends this tab's session, a login elsewhere logs it in.
  // Key null means localStorage was cleared.
  const logInRef = useRef(logIn);
  logInRef.current = logIn;
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== null && e.key !== LOGIN_MARKER_KEY) return;
      const marker = readLoginMarker();
      if (marker === loginIdRef.current) return;
      if (marker) {
        logInRef.current(marker);
      } else {
        endLocalSession();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [endLocalSession]);

  return {
    loginId, logIn, logOut, setSessionLimits,
    secondsLeft, staySignedIn,
    sessionExpiresAt, sessionEndNoticeShown, dismissSessionEndNotice,
  };
}
